import Image from 'next/image';
import Link from 'next/link';
import {NextSeo} from 'next-seo';

import {TrustedBySection} from 'components/HeroSection';
import {trackScheduleACallClick, trackSignUpClick} from 'utils/analytics';
import GradientContainer from 'components/GradientContainer';
import GetStartedFooterSection from 'components/GetStartedFooterSection';

const UnderstandIncidents = () => (
  <>
    <NextSeo title="Understand Incidents | Polar Signals" />
    <main>
      <GradientContainer className="align-center flex flex-col justify-start">
        <div className="relative mx-auto mb-8 mt-[125px] flex flex-col items-center gap-12 px-4 text-white lg:container lg:mt-[140px] lg:mb-8 lg:flex-row lg:items-center lg:justify-center lg:py-5">
          <div className="flex flex-col items-center justify-center gap-y-8 lg:w-[600px] lg:items-start lg:justify-start lg:gap-y-7">
            <div className="text-center text-4xl font-semibold leading-tight lg:text-left lg:text-[50px]">
              Understand Incidents
            </div>
            <div className="max-w-[422px] whitespace-pre-line text-center text-base leading-6 text-gray-200 lg:max-w-prose lg:text-left lg:text-xl lg:leading-7">
              Travel back in time to pinpoint incidents and issues. Profiling data provides unique
              insight and depth into what a process executed over time.
            </div>
            <div className="flex flex-col gap-3 lg:flex-row">
              <a
                href="https://cloud.polarsignals.com/getting-started/"
                className="flex w-full items-center justify-center rounded bg-white px-10 py-3 text-center font-sans text-base font-medium text-gray-700 first-line:text-center hover:opacity-90 lg:w-fit lg:py-4 lg:text-lg"
                onClick={() => trackSignUpClick('understand-incidents-hero-cta')}
              >
                <span>Start free 14-day trial</span>
              </a>
              <Link href="/schedule-a-call">
                <a
                  className="flex w-full items-center justify-center rounded border-2 border-white px-10 py-3 text-center font-sans text-base font-medium text-white first-line:text-center hover:bg-white hover:text-gray-700 hover:opacity-90 lg:w-fit lg:py-4 lg:text-lg"
                  onClick={() => trackScheduleACallClick('understand-incidents-hero-cta')}
                >
                  <span>Book a demo</span>
                </a>
              </Link>
            </div>
          </div>
          <div className="relative mt-8 flex h-[250px] w-full items-center justify-center md:h-[400px] lg:m-0 lg:h-[500px] lg:w-[600px]">
            <Image
              loading="lazy"
              src="/use-cases/understand-incidents-hero-image.png"
              alt="Understand Incidents"
              width={600}
              height={500}
              layout="fill"
            />
          </div>
        </div>
        <section className="mt-16 lg:mt-32">
          <TrustedBySection />
        </section>
      </GradientContainer>
      <section className="container mx-auto flex flex-col items-center justify-center gap-12 px-4 py-12 md:px-12 lg:flex-row lg:gap-20 lg:py-32">
        <div className="flex flex-col gap-10">
          <div>
            <p className="font-dazzed text-2xl font-semibold text-gray-800">
              Resolve Incidents Faster
            </p>
            <p className="!mb-0 max-w-[500px] text-base text-gray-700">
              With Polar Signals Cloud you always have all performance data available across your
              entire infrastructure and throughout time. This removes the guesswork and instead
              allows you to focus on the fix. Understand the difference between version A and B, or
              two points in time, or two Kubernetes clusters!.
            </p>
          </div>

          <div>
            <p className="font-dazzed text-2xl font-semibold text-gray-800">
              Incident Root Cause Analysis
            </p>
            <p className="!mb-0 max-w-[500px] text-base text-gray-700">
              Polar Signals Cloud captures detailed information about the execution of your
              application code. We provide insights into the specific functions, methods, or lines
              of code that are consuming excessive resources or causing performance issues.
            </p>
          </div>
        </div>

        <div>
          <Image
            loading="lazy"
            src="/use-cases/understand-incidents-section-image.png"
            alt="An illustration of a Polar Signals Dashboard comparing two different versions of a service"
            width={600}
            height={420}
          />
        </div>
      </section>
      <section className="container mx-auto flex flex-col items-center justify-center gap-6 px-4 py-12 md:px-12 lg:py-32">
        <div className="relative h-[32px] w-[135px]">
          <Image src="/logos/indent-dark.png" alt="Indent logo" layout="fill" objectFit="contain" />
        </div>

        <p className="!mb-0 max-w-[800px] text-center text-xl">
          “By making the root cause for most performance issues painfully obvious Polar Signals
          Cloud lets our engineering teams skip the debate about why so they can focus on the fix.”
        </p>
        <div className="flex gap-3">
          <Image
            src="/testimonials/dan-gillespie.png"
            alt="A picture of Dan Gillespie."
            className="h-16 object-contain"
            height={65}
            width={65}
          />
          <div>
            <p className="!mb-0 font-semibold">Dan Gillespie</p>
            <p className="!mb-0">
              CTO, <span className="font-semibold">Indent</span>
            </p>
          </div>
        </div>
      </section>
      <GetStartedFooterSection />
    </main>
  </>
);

export default UnderstandIncidents;
